/**
 * Keeps the Orbe geolocation app in sync with the theme's language selector.
 *
 * Orbe (app embed `orbe-geolocation/blocks/app-embed-modal`) remembers the language the visitor picked in its
 * modal in the cookie `mdApp_showRecommendationLang`. On every page load it runs, in orbe.min.js:
 *
 *   let t = this.getCookie('mdApp_showRecommendationLang');
 *   if (void 0 !== t && this.currentLocaleCode === t) return true;   // cookie matches -> Orbe stands down
 *   ... e.dataset.value === t && (this._setLanguageSelector(e), this.elements.modalSubmitButton.click())
 *
 * `currentLocaleCode` is `window.Shopify.locale`. So when the theme's selector switches DE -> FR, the cookie
 * still says "de", Orbe sees a mismatch and auto-clicks its own submit button, redirecting straight back to
 * German. The switch appears to do nothing.
 *
 * Writing the cookie as part of the language switch makes Orbe's first check pass, so it leaves the choice
 * alone. This is a delegated listener on the localization form, so it also covers the footer selector and any
 * other `/localization` form in the theme.
 */
(() => {
  const COOKIE_NAME = 'mdApp_showRecommendationLang';

  // Orbe writes this cookie with `max-age = 86400 * cookies.modal_reappear`, default 60 days.
  const MAX_AGE_SECONDS = 60 * 86400;

  let lastClickedLocale = null;

  /**
   * Orbe omits the domain attribute for a same-domain switch but sets `domain=<host>` when it moves the
   * visitor across domains, so the cookie can exist at either scope. Writing both keeps them agreeing —
   * document.cookie reads are ambiguous when a name exists twice, and Orbe's getCookie() takes the first hit.
   */
  const sharedCookieDomain = () => {
    const labels = window.location.hostname.split('.');
    if (labels.length < 2) return null;

    const domain = `.${labels.slice(-2).join('.')}`;
    // myshopify.com is a public suffix; browsers reject a cookie scoped to it, and Orbe skips it too.
    return domain === '.myshopify.com' ? null : domain;
  };

  const rememberLocale = (locale) => {
    // Locale codes ("de", "fr", "pt-BR") need no escaping, and Orbe reads the value raw.
    const cookie = `${COOKIE_NAME}=${locale};path=/;max-age=${MAX_AGE_SECONDS};`;
    document.cookie = cookie;

    const domain = sharedCookieDomain();
    if (domain) {
      document.cookie = `${cookie}domain=${domain};`;
    }
  };

  const localeFromSubmitter = (event, form) => {
    // event.submitter is unsupported on older Safari, hence the click fallback.
    const submitter = event.submitter || null;
    if (submitter && submitter.name === 'locale_code' && submitter.value) return submitter.value;
    if (lastClickedLocale) return lastClickedLocale;

    const input = form.querySelector('[name="locale_code"]');
    return input ? input.value : null;
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[name="locale_code"]');
      lastClickedLocale = trigger ? trigger.value : null;
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.action.includes('/localization')) return;

      const locale = localeFromSubmitter(event, form);
      if (locale) rememberLocale(locale);
    },
    true
  );
})();
